import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  useLazyGetDesksQuery,
  useGetDesksQuery,
} from "../redux/slices/api.slice";
import { Loader, MultiSelect, Radio } from "@mantine/core";
import CustomSelect from "./CustomSelect";
import { DeskOptionProps, desksType } from "../interfaces/reports";

const getLeadingNumber = (value) => {
  const num = parseInt(value.split("-")[0]);
  return Number.isNaN(num) ? null : num;
};

const getTrailingNumber = (value) => {
  const match = value.match(/T(\d+)/);
  return match ? parseInt(match[1]) : null;
};

const sortDesks = (desks) => {
  const numericVals = [];
  const nonNumericVals = [];

  for (const desk of desks) {
    getLeadingNumber(desk.value) !== null ? numericVals.push(desk) : nonNumericVals.push(desk);
  }

  numericVals.sort((a, b) => getLeadingNumber(a.value) - getLeadingNumber(b.value));
  nonNumericVals.sort((a, b) => getTrailingNumber(a.value) - getTrailingNumber(b.value));

  return [...numericVals, ...nonNumericVals];
};

const DeskOption = ({
  floorID,
  selection,
  desksSelection,
  setDeskSelection,
  deskMap,
  setDeskMap,
  deskRange,
  // setDeskAlias,
  floorName,
  setDeskRange,
}) => {
  const { data: allDesks, isLoading: isLoadingDesks, isFetching: isFetchingDesks, } = useGetDesksQuery(floorID);
  const [desks, setDesks] = useState(null);

 
  const sortedDesks = useMemo(() => {
    if (allDesks?.length > 0) {
      const updated = allDesks.map((desk) => ({ value: desk.id, label: desk.alias }));
      return { [allDesks[0].floorID]: sortDesks(updated) };
    }
    return null;
  }, [allDesks]);

 
  useEffect(() => {
    if (sortedDesks) {
      setDesks((prev) => ({ ...prev, ...sortedDesks }));
    }
  }, [sortedDesks]);

  
  useEffect(() => {
    if (desks && selection?.startDeskID && selection?.endDeskID) {
      const floorDesks = desks[floorID];
      if (!floorDesks) return;

      const startIndex = floorDesks.findIndex((el) => el.value === selection.startDeskID);
      const endIndex = floorDesks.findIndex((el) => el.value === selection.endDeskID);
      if (startIndex === -1 || endIndex === -1) return;

      const [min, max] = [Math.min(startIndex, endIndex), Math.max(startIndex, endIndex)];
      const updatedRange = floorDesks.slice(min, max + 1).map((el) => el.value);

      setDeskRange((prev) => ({ ...prev, [floorID]: updatedRange }));
    }
  }, [selection, desks, floorID, setDeskRange]);

 
  useEffect(() => {
    if (selection && desks) {
      const updatedMap = {
        all: () => desks[floorID]?.map((desk) => desk.value) || [],
        single: () => [selection.singleDeskId],
        range: () => deskRange[floorID] || [],
      }[selection.deskOption];

      if (updatedMap) {
        setDeskMap((prev) => ({ ...prev, [floorID]: updatedMap() }));
      }
    }
  }, [deskRange, selection, desks, floorID, setDeskMap]);

  const handleDeskOptionChange = useCallback(
    (option, value, position) => {
      if (option !== "range") {
        setDeskRange((prev) => ({ ...prev, [floorID]: [] }));
        setDeskSelection({
          ...desksSelection,
          [floorID]: {
            ...selection,
            deskOption: option,
            singleDeskId:"",
            startDeskID: "",
            endDeskID: "",
          },
        });
        return
      }
  
      setDeskSelection({
        ...desksSelection,
        [floorID]: {
          ...selection,
          deskOption: option,
          singleDeskId: option === "single" ? value : "",
          startDeskID: option === "range" && position === "start" ? value : selection.startDeskID,
          endDeskID: option === "range" && position === "end" ? value : selection.endDeskID,
        },
      });
    },
    [desksSelection, selection, floorID, setDeskRange, setDeskSelection]
  );
  
  return (
    <div>
      <div>
        <div className="flex gap-2 items-center mb-2">
          <p className="text-base font-medium mb-1">
            Selected Desks for
            <span className="font-[600] pl-1">{floorName}</span>
          </p>
        </div>
        <div className={"flex flex-col gap-4"}>
          <Doption
            label="All Desks"
            description="Choosing this option will include all desks on the selected floor"
            checked={selection.deskOption === "all"}
            onChange={() => handleDeskOptionChange("all", "", "")}
            disable={!floorID}
          />

      {/*     <Doption
            label="Single Desk"
            description="Select which desk to include"
            checked={selection.deskOption === "single"}
            onChange={(value) => {}}
            disable={!floorID}
            customSelectProps={{
              data: desks?.[floorID] || [],
              value: selection.singleDeskId,
              setValue: (value) => handleDeskOptionChange("single", value, ""),
              placeholder: "",
              isLoading: isFetchingDesks,
            }}
          /> */}

          <Doption
            label="Range of Desks"
            description="Select the starting and ending positions for your desks"
            checked={selection.deskOption === "range"}
            onChange={() => handleDeskOptionChange("range", selection.startDeskID, "start")}
            disable={!floorID}
            customSelectProps={{
              data: desks?.[floorID] || [],
              value: selection.startDeskID,
              setValue: (value) =>
                handleDeskOptionChange("range", value, "start"),
              placeholder: "starting at",
              isLoading: isFetchingDesks,
            }}
            customSelectPropsEnd={{
              data: desks?.[floorID] || [],
              value: selection.endDeskID,
              setValue: (value) =>
                handleDeskOptionChange("range", value, "end"),
              placeholder: "ending at",
              isLoading: isFetchingDesks,
            }}
            range={true}
            desks={desks}
            floorID={floorID}
            deskRange={deskRange}
            setDeskRange={setDeskRange}
            setDeskSelection={setDeskSelection}
          />
        </div>
      </div>
    </div>
  );
};

export default DeskOption;

const Doption = ({
  label,
  description,
  checked,
  onChange,
  disable,
  customSelectProps,
  customSelectPropsEnd,
  range,
  desks,
  floorID,
  deskRange,
  setDeskRange,
  setDeskSelection,
}: DeskOptionProps) => {
  return (
    <div
      className={`flex  gap-6 border border-solid rounded-lg p-4 ${
        checked ? "border-action" : "border-grayText4"
      }`}
    >
      <Radio
      onChange={onChange}
        checked={checked}
        readOnly
        color="#FB6514"
        disabled={disable}
        className="mt-1 "
      />
      <div className="flex-1">
        <p>{label}</p>
        <p className="text-grayText3 text-sm mt-1">{description}</p>
        <div className="flex sm:flex-row flex-col sm:items-start sm:flex-wrap gap-2 mt-2 w-full">
          {customSelectProps && (
            <>
              {customSelectProps.placeholder && (
                <p className="text-grayText3 text-sm hidden sm:block whitespace-nowrap">
                  {customSelectProps.placeholder}
                </p>
              )}
              <div className="flex-1 min-w-0">
                <CustomSelect {...customSelectProps} />
              </div>
            </>
          )}
          {customSelectPropsEnd && (
            <>
              <p className="text-grayText3 text-sm hidden sm:block whitespace-nowrap">
                {customSelectPropsEnd.placeholder}
              </p>
              <div className="flex-1 min-w-0">
                <CustomSelect {...customSelectPropsEnd} />
              </div>
            </>
          )}
        </div>
        {range && (
          <div className="flex flex-col  w-full ">
            <p className="text-grayText3 text-sm mt-3 mb-2">
              You can also add or remove desks individually
            </p>

            <MultiSelect
              data={desks?.[floorID] || []}
              value={deskRange[floorID]}
              className="w-full"
              onChange={(value) => {
                setDeskRange((prev) => ({
                  ...prev,
                  [floorID]: value,
                }));
                setDeskSelection((prev) => ({
                  ...prev,
                  [floorID]: {
                    startDeskID: "",
                    endDeskID: "",
                    singleDeskId: "",
                    deskOption: "range",
                  },
                }));
              }}
              withScrollArea={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};
